/** Presentation helpers shared by the API's console output and the web UI. */

/**
 * The locale every formatter in this file reads.
 *
 * Module state rather than an argument, because there is exactly one language on
 * screen at a time and threading a locale through the hundred-odd call sites
 * would put it in the signature of every component that shows a number. The web
 * app calls `setFormatLocale` once, whenever the language changes.
 *
 * The API imports these helpers too, for its console reports, where the default
 * is the right answer and nothing ever calls the setter.
 */
let locale = "en-GB";

export function setFormatLocale(next: string): void {
  locale = next;
}

export function getFormatLocale(): string {
  return locale;
}

export const formatNumber = (n: number) => Math.round(n).toLocaleString(locale);

export function formatMoney(n: number, currency: string, fractionDigits = 0): string {
  return n.toLocaleString(locale, {
    style: "currency",
    currency,
    // Spanish renders EUR as the three-letter code by default, which is both
    // wider and less legible than the symbol in a column of figures.
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/**
 * Compact impressions for tight mobile layouts: 1.8M, 502k, 973.
 *
 * Hand-rolled rather than `notation: "compact"`, which renders a lowercase "m"
 * in English and inserts a space in Spanish — both of which cost more width than
 * these numbers have. The suffixes are the same in both languages, and both put
 * the decimal point in the same place, so only the digits need localising.
 */
export function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${round(n / 1_000_000, n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${round(n / 1_000, n >= 100_000 ? 0 : 1)}k`;
  return formatNumber(n);
}

/**
 * Money at headline size: €16.3k, €9.7k, €430.
 *
 * The digits come from `formatCompact` so a compacted spend and a compacted
 * impression count round the same way and read as the same kind of number. Only
 * the currency's own decoration is taken from Intl, via `formatToParts`, because
 * where the symbol sits and whether it takes a space is a per-locale fact —
 * "€16.3k" in English, "16,3k €" in Spanish — and hand-placing it would get one
 * of the two wrong.
 *
 * Below a thousand there is nothing to compact, so the exact amount is shown.
 */
export function formatMoneyCompact(n: number, currency: string): string {
  if (Math.abs(n) < 1_000) return formatMoney(n, currency);

  const digits = formatCompact(n);
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).formatToParts(n);

  let out = "";
  let placed = false;
  for (const part of parts) {
    if (NUMERIC_PARTS.has(part.type)) {
      if (!placed) {
        out += digits;
        placed = true;
      }
      continue;
    }
    out += part.value;
  }
  return out;
}

/** The parts of a formatted amount that the compact digits stand in for. */
const NUMERIC_PARTS = new Set<Intl.NumberFormatPartTypes>([
  "integer",
  "group",
  "decimal",
  "fraction",
  "minusSign",
]);

function round(n: number, digits: number): string {
  return n.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export const formatRoas = (roas: number | null) =>
  roas == null ? "—" : `${round(roas, 2)}x`;

export const formatPercent = (fraction: number, digits = 1) =>
  fraction.toLocaleString(locale, {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/**
 * Hook and hold rate. A dash when the rate is absent or has no impressions
 * behind it, since neither case is a real 0%.
 */
export const formatRate = (fraction: number | null | undefined, digits = 1) =>
  fraction == null ? "—" : formatPercent(fraction, digits);

/**
 * A position in a clip, as "0:07" or "1:04".
 *
 * Sub-minute values keep the leading zero rather than reading as bare seconds,
 * because the numbers sit on a timeline where "7" and "1:07" must be instantly
 * distinguishable.
 */
export function formatTimecode(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Stored calendar dates are parsed and formatted in UTC throughout, so a date
 * renders as itself rather than shifting a day for viewers west of the meridian.
 */
function atUtc(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

/** A date as "12 Jul" / "12 jul", for launch dates sitting beside a creative. */
export function formatShortDate(date: string): string {
  return atUtc(date).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** The same with a year, for settings screens where the year is in question. */
export function formatLongDate(date: string): string {
  return atUtc(date).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * A span of days as "16 – 30 Jul" or "30 Jul – 2 Aug".
 *
 * `formatRange` collapses whatever the two dates have in common, so a range
 * inside one month names the month once. Doing that by hand would mean deciding,
 * per language, which part is shared and where the month sits relative to the
 * day — which is exactly the knowledge Intl already has.
 */
export function formatDateRange(from: string, to: string, withYear = false): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  }).formatRange(atUtc(from), atUtc(to));
}

/**
 * A wall-clock moment — a sync that started, a pause that expires.
 *
 * Distinct from the date helpers above, which format stored calendar dates in
 * UTC. These are real instants and are shown in the reader's own timezone,
 * because "did that run recently" is a question about the reader's clock.
 */
export function formatDateTime(at: Date | string, opts?: Intl.DateTimeFormatOptions): string {
  return new Date(at).toLocaleString(locale, opts ?? { dateStyle: "short", timeStyle: "medium" });
}

export function formatTime(at: Date | string): string {
  return new Date(at).toLocaleTimeString(locale, { timeStyle: "medium" });
}

/**
 * How long ago something happened, as "5 min ago" / "hace 5 min".
 *
 * Callers handle anything under a minute themselves: "0 minutes ago" is a worse
 * answer than "just now" in every language, and which unit reads best that close
 * to the present is a copy decision rather than a formatting one.
 */
export function formatRelativeTime(ms: number): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "short" });
  const minutes = Math.round(ms / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
  return rtf.format(-Math.round(hours / 24), "day");
}

/**
 * Split a daily series into finished days and the one still being delivered.
 *
 * Today is always a fraction of a day, so plotted beside full days it draws a
 * cliff that has nothing to do with the creative — at 3am every video in the
 * account looks like it just died. Charts draw the finished days as the real
 * line and mark the last one as provisional rather than dropping it, since the
 * number is correct, just incomplete.
 */
export function splitFinishedDays<T extends { date: string }>(
  series: T[],
  today: string,
): { finished: T[]; inProgress: T | null } {
  const last = series.at(-1);
  if (!last || last.date !== today) return { finished: series, inProgress: null };
  return { finished: series.slice(0, -1), inProgress: last };
}

export type RoasTier = "good" | "ok" | "bad" | "none";

/**
 * ROAS colour tiers. `greenThreshold` is configurable because what counts as
 * a good return is a business decision, not a constant.
 */
export function roasTier(roas: number | null, greenThreshold: number): RoasTier {
  if (roas == null) return "none";
  if (roas >= greenThreshold) return "good";
  if (roas >= 1) return "ok";
  return "bad";
}
